/**
 * The pool of instanced meshes, one per prop kind, and how props are placed on the planet. A
 * placement records which tile it belongs to, so a pop rescales just that tile's instances.
 */
import * as THREE from 'three';

import { easeBack } from '@core/math';

import type { PropKind } from './catalog';
import { UP, X } from './geometry';

const POP_MS = 600;

interface Placed {
  kind: string;
  index: number;
  matrix: THREE.Matrix4;
}

export interface Instances {
  /** Start a rebuild on a planet of radius `r0`: every mesh empty, nothing remembered. */
  begin(r0: number): void;
  /** The tile the next placements belong to (-1: none), and the height they stand on. */
  at(tile: number, h0: number): void;
  /** One instance on the planet at unit direction `c`, moved (dx, dz) along the ground, turned by `yaw`. */
  put(
    name: string,
    c: THREE.Vector3,
    dx: number,
    dz: number,
    yaw: number,
    scale: number,
    color?: THREE.Color,
    dy?: number,
  ): number;
  /** One instance from a whole matrix; its index, or -1 when the mesh is full. */
  putMatrix(name: string, matrix: THREE.Matrix4, color?: THREE.Color): number;
  /** Close a rebuild: the meshes draw what was placed. */
  end(): void;
  /** The radius of the planet being built. */
  radius(): number;

  pop(tile: number, now: number): void;
  /** Advance pops; true while one is still going. */
  animatePops(now: number): boolean;

  /** For animation outside rebuilds: move an instance, or show only the first `count`. */
  setMatrixAt(name: string, index: number, matrix: THREE.Matrix4): void;
  setCount(name: string, count: number): void;
  capacity(name: string): number;
  /** Draws kind `name` with another model from now on: the art replacing its stand-in. */
  setGeometry(name: string, geometry: THREE.BufferGeometry): void;
  /** Upload what moved since the last flush. */
  flush(): void;
}

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _t1 = new THREE.Vector3();
const _t2 = new THREE.Vector3();

/** Two directions along the ground at unit direction `c`. */
function tangents(c: THREE.Vector3): void {
  const a = Math.abs(c.y) < 0.9 ? UP : X;
  _t1.crossVectors(a, c).normalize();
  _t2.crossVectors(c, _t1).normalize();
}

export function createInstances(
  parent: THREE.Group,
  kinds: Readonly<Record<string, PropKind>>,
): Instances {
  const meshes = new Map<string, THREE.InstancedMesh>();
  for (const [name, kind] of Object.entries(kinds)) {
    const mesh = new THREE.InstancedMesh(kind.geometry, kind.material, kind.max);
    mesh.name = name;
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.castShadow = kind.shadow;
    if (kind.colored)
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(kind.max * 3), 3);
    meshes.set(name, mesh);
    parent.add(mesh);
  }
  const counts = new Map<string, number>();
  const byTile = new Map<number, Placed[]>();
  const pops = new Map<number, number>();
  const touched = new Set<string>();
  let r0 = 1;
  let h0 = 0;
  let tile = -1;

  const putMatrix = (name: string, matrix: THREE.Matrix4, color?: THREE.Color): number => {
    const mesh = meshes.get(name);
    if (!mesh) return -1;
    const n = counts.get(name) ?? 0;
    if (n >= mesh.instanceMatrix.count) return -1;
    mesh.setMatrixAt(n, matrix);
    if (color && mesh.instanceColor) mesh.setColorAt(n, color);
    counts.set(name, n + 1);
    if (tile >= 0) {
      let list = byTile.get(tile);
      if (!list) {
        list = [];
        byTile.set(tile, list);
      }
      list.push({ kind: name, index: n, matrix: matrix.clone() });
    }
    return n;
  };

  return {
    begin(radius) {
      r0 = radius;
      counts.clear();
      byTile.clear();
      tile = -1;
      h0 = 0;
    },
    at(t, height) {
      tile = t;
      h0 = height;
    },
    put(name, c, dx, dz, yaw, scale, color, dy = 0) {
      tangents(c);
      _p.copy(c)
        .multiplyScalar(r0 + h0 + dy)
        .addScaledVector(_t1, dx)
        .addScaledVector(_t2, dz);
      _q.setFromUnitVectors(UP, c);
      _q2.setFromAxisAngle(UP, yaw);
      _q.multiply(_q2);
      _s.setScalar(Math.max(0.0001, scale));
      _m.compose(_p, _q, _s);
      return putMatrix(name, _m, color);
    },
    putMatrix,
    end() {
      for (const [name, mesh] of meshes) {
        mesh.count = counts.get(name) ?? 0;
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      }
    },
    radius: () => r0,
    pop(t, now) {
      if (byTile.has(t)) pops.set(t, now);
    },
    animatePops(now) {
      for (const [t, t0] of pops) {
        const p = Math.min(1, (now - t0) / POP_MS);
        const scale = p < 1 ? easeBack(p) : 1;
        for (const placed of byTile.get(t) ?? []) {
          const mesh = meshes.get(placed.kind);
          if (!mesh) continue;
          _m.copy(placed.matrix);
          if (scale !== 1) {
            _m.decompose(_p, _q, _s);
            _s.multiplyScalar(Math.max(0.0001, scale));
            _m.compose(_p, _q, _s);
          }
          mesh.setMatrixAt(placed.index, _m);
          touched.add(placed.kind);
        }
        if (p >= 1) pops.delete(t);
      }
      return pops.size > 0;
    },
    setMatrixAt(name, index, matrix) {
      const mesh = meshes.get(name);
      if (!mesh) return;
      mesh.setMatrixAt(index, matrix);
      touched.add(name);
    },
    setCount(name, count) {
      const mesh = meshes.get(name);
      if (!mesh || (mesh.count === count && count === 0)) return;
      mesh.count = count;
      touched.add(name);
    },
    capacity: (name) => meshes.get(name)?.instanceMatrix.count ?? 0,
    setGeometry(name, geometry) {
      const mesh = meshes.get(name);
      if (mesh) mesh.geometry = geometry;
    },
    flush() {
      for (const name of touched) {
        const mesh = meshes.get(name);
        if (mesh) mesh.instanceMatrix.needsUpdate = true;
      }
      touched.clear();
    },
  };
}
